/**
 * AM-026 Rule RBAC-03 — User Permission Overrides Modal.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PermissionOverrideEffect,
  useUpdateUserPermissions,
  useUserPermissions,
} from '../api/am026';

interface UserPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userDisplayName: string;
}

const DOMAIN_FA: Record<string, string> = {
  inventory: "انبار",
  production: "تولید",
  quality: "کیفیت",
  maintenance: "نگهداری و تعمیرات",
  engineering: "مهندسی",
  planning: "برنامه‌ریزی",
  purchasing: "خرید و بازرگانی",
  sales: "فروش",
  finance: "امور مالی",
  users: "کاربران",
  tenant: "تنانت (مستأجر)",
  organizations: "ساختار سازمانی",
  reporting: "گزارش‌گیری",
  workflow: "گردش کار",
  marketplace: "بازارچه",
  imports: "واردات داده",
};

const ACTION_FA: Record<string, string> = {
  view: "مشاهده",
  manage: "مدیریت",
  export: "خروجی اطلاعات",
  import: "واردات اطلاعات",
  import_data: "واردات گروهی داده‌ها",
  post_opening_balance: "ثبت موجودی اولیه",
  execute: "اجرا",
  reset_password: "ریست رمز عبور",
  permissions: "مدیریت دسترسی‌ها",
};

const PERMISSION_LABELS_FA: Record<string, string> = {
  "inventory.view": "مشاهده انبار",
  "inventory.manage": "مدیریت انبار",
  "inventory.export": "خروجی اطلاعات انبار",
  "inventory.post_opening_balance": "ثبت موجودی اولیه انبار",
  "imports.import_data": "واردات گروهی داده‌ها",
  "production.view": "مشاهده خط تولید",
  "production.manage": "مدیریت تولید",
  "quality.view": "مشاهده کیفیت",
  "quality.manage": "مدیریت کیفیت",
  "maintenance.view": "مشاهده نگهداری و تعمیرات",
  "maintenance.manage": "مدیریت نگهداری و تعمیرات",
  "engineering.view": "مشاهده مهندسی",
  "engineering.manage": "مدیریت مهندسی",
  "users.view": "مشاهده کاربران",
  "users.manage": "مدیریت کاربران و دسترسی‌ها",
  "tenant.manage": "مدیریت مستأجر",
  "organizations.view": "مشاهده ساختار سازمانی",
  "organizations.manage": "مدیریت ساختار سازمانی",
};

function formatPermissionTitle(code: string, fallbackLabel: string): string {
  if (PERMISSION_LABELS_FA[code]) return PERMISSION_LABELS_FA[code];
  if (code.includes(".")) {
    const [domain, action] = code.split(".", 2);
    if (!domain || !action) return fallbackLabel || code;
    const domainName = DOMAIN_FA[domain] || domain;
    const actionName = ACTION_FA[action] || action.replace("_", " ");
    return `${actionName} ${domainName}`;
  }
  return fallbackLabel || code;
}

export function UserPermissionsModal({
  open,
  onClose,
  userId,
  userDisplayName,
}: UserPermissionsModalProps) {
  const { data, isLoading, isError } = useUserPermissions(userId);
  const { mutate, isPending } = useUpdateUserPermissions(userId);

  const [localOverrides, setLocalOverrides] = useState<
    Record<string, PermissionOverrideEffect>
  >({});

  useEffect(() => {
    if (data) {
      const initial: Record<string, PermissionOverrideEffect> = {};
      for (const row of data) {
        initial[row.permission_code] = row.override;
      }
      setLocalOverrides(initial);
    }
  }, [data]);

  if (!open) return null;

  const handleChange = (code: string, effect: PermissionOverrideEffect) => {
    setLocalOverrides((prev) => ({ ...prev, [code]: effect }));
  };

  const handleSave = () => {
    const overrides = Object.entries(localOverrides).map(([permission_code, effect]) => ({
      permission_code,
      effect,
    }));
    mutate({ overrides }, { onSuccess: onClose });
  };

  const getLabel = (row: { permission_code: string; label: string }) => {
    return formatPermissionTitle(row.permission_code, row.label);
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "1rem",
      }}
    >
      <div
        role="dialog"
        aria-label={`دسترسی‌های ${userDisplayName}`}
        className="am026-modal"
        style={{
          background: "var(--color-surface, #ffffff)",
          border: "1px solid var(--color-border, #e2e8f0)",
          borderRadius: "14px",
          padding: "1.5rem",
          width: "100%",
          maxWidth: "650px",
          maxHeight: "85vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15)",
          color: "var(--color-text-main)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--color-border, #f1f5f9)", paddingBottom: "0.5rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: "700" }}>⚙️ مدیریت دسترسی‌های {userDisplayName}</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.1rem", cursor: "pointer" }}>✕</button>
        </div>

      {isLoading && <p>در حال بارگذاری...</p>}
      {isError && <p role="alert">خطا در دریافت دسترسی‌ها.</p>}

      {data && (
        <table>
          <thead>
            <tr>
              <th>دسترسی</th>
              <th>از نقش</th>
              <th>وضعیت</th>
              <th>مؤثر</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.permission_code}>
                <td>{getLabel(row)}</td>
                <td>{row.from_role ? '✓' : '—'}</td>
                <td>
                  <select
                    aria-label={`وضعیت دسترسی ${row.label}`}
                    value={localOverrides[row.permission_code] ?? 'none'}
                    onChange={(e) =>
                      handleChange(
                        row.permission_code,
                        e.target.value as PermissionOverrideEffect,
                      )
                    }
                  >
                    <option value="none">پیش‌فرض (از نقش)</option>
                    <option value="grant">فعال (Grant)</option>
                    <option value="deny">غیرفعال (Deny)</option>
                  </select>
                </td>
                <td>{row.effective ? '✅' : '❌'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="am026-modal-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
        <button type="button" onClick={onClose} disabled={isPending} style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.4rem 1rem", fontSize: "0.85rem", cursor: "pointer" }}>
          انصراف
        </button>
        <button type="button" onClick={handleSave} disabled={isPending || !data} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", padding: "0.4rem 1rem", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}>
          {isPending ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
        </button>
      </div>
    </div>
    </div>,
    document.body
  );
}
